import { makeAutoObservable } from "mobx"
import React from "react"

import {
    message_CreateMessageRequest,
    message_CreateMessageAttachment,
    message_MessageAttachment,
    message_UpdateMessage,
    MessageAdminService,
    PreviewService,
} from "src/api"
import { Channel } from "src/channel"
import { loads } from "src/channel/utils"
import { environment, DEFAULT_ACCESS_GROUP } from "src/config"
import { parseDate } from "src/lib/date"
import { persistFiles } from "src/lib/file"
import { FormFields } from "src/lib/form-fields"
import { createLoadingKeys } from "src/lib/loading"
import {
    IAccessGroups,
    IFormFields,
} from "src/modals/notice-board-post-detail/types"

type IPersistedNoticeBoardPostFile = Omit<IPersistedFile, "type"> & {
    type: "image" | "doc"
}

const flutterBaseUrl = environment.FLUTTER_BASE_URL

export class NoticeBoardPostDetailStore {
    static Context = React.createContext<NoticeBoardPostDetailStore | null>(
        null,
    )
    static LoadingKeys = createLoadingKeys("init", "submit", "preview")
    fields = new FormFields<IFormFields>({
        admin_title: "",
        title: "",
        text: "",
        access_type: "",
        pinned: false,
        send_push: true,
        publish_at: null,
        unpublish_at: null,
        event_ends_at: null,
        event_starts_at: null,
        include_in_chatbot: false,
        segment_ids: [],
        imageAttachments: [],
        documentAttachments: [],
        accessGroupId: undefined,
        dimensionIds: [],
    })

    private id?: number
    private _accessGroups?: IAccessGroups[]

    constructor() {
        makeAutoObservable(this)
    }

    private isValidAccessGroup = (groupId: number): boolean =>
        this._accessGroups?.some((x) => x.id === groupId) ?? false

    get isEditMode() {
        return this.id != null
    }

    setAccessGroups(accessGroups: IAccessGroups[]) {
        this._accessGroups = accessGroups
    }

    determineAccessGroupId = (
        mode: string | undefined,
        responseAccessGroupId: number,
        accessGroupId: number,
    ) => {
        if (mode === "Copy") {
            this.fields.setError("accessGroupId", "")
            return accessGroupId
        }
        return this.isValidAccessGroup(responseAccessGroupId)
            ? responseAccessGroupId
            : accessGroupId
    }

    @loads(() => NoticeBoardPostDetailStore.LoadingKeys.init)
    async init(accessGroupId: number, id?: number, mode?: string) {
        if (mode !== "Copy") {
            this.setId(id)
        }
        const initialFields =
            id != null
                ? await this.fetchExistingMessage(id, mode, accessGroupId)
                : this.getDefaultFields(accessGroupId)

        this.fields.init(initialFields)
    }

    private async fetchExistingMessage(
        id: number,
        mode?: string,
        accessGroupId?: number,
    ): Promise<IFormFields> {
        const response = await MessageAdminService.noticeBoardGetMessage({
            messageId: id,
        })

        return {
            admin_title: response.admin_title ?? "",
            title: response.title ?? "",
            text: response.text ?? "",
            pinned: response.pinned ?? false,
            access_type:
                mode === "Copy" ? "WRITER" : response.access_type ?? "",
            send_push: response.send_push ?? false,
            publish_at: parseDate(response.publish_at),
            unpublish_at: parseDate(response.unpublish_at),
            event_starts_at: parseDate(response.event_starts_at),
            event_ends_at: parseDate(response.event_ends_at),
            segment_ids: response.segment_ids ?? [],
            dimensionIds: response.dimension_ids ?? [],
            imageAttachments: this.toPersistedFiles(
                response.attachments ?? [],
                "image",
            ),
            documentAttachments: this.toPersistedFiles(
                response.attachments ?? [],
                "doc",
            ),
            include_in_chatbot: response.include_in_chatbot ?? false,
            accessGroupId: this.determineAccessGroupId(
                mode,
                response.access_group_id,
                accessGroupId ?? DEFAULT_ACCESS_GROUP.id,
            ),
        }
    }

    private getDefaultFields(accessGroupId: number): IFormFields {
        return {
            admin_title: "",
            title: "",
            text: "",
            pinned: false,
            access_type: "",
            send_push: false,
            publish_at: null,
            unpublish_at: null,
            event_starts_at: null,
            event_ends_at: null,
            segment_ids: [],
            imageAttachments: [],
            documentAttachments: [],
            include_in_chatbot: false,
            accessGroupId,
        }
    }

    getDetailsPreviewUrl = async () => {
        const detailsPreviewUrl =
            flutterBaseUrl + "notice-board-details-preview?base64="

        try {
            const url = await PreviewService.postV1PreviewMessage({
                request: {
                    attachments: await this.getAttachments(),
                    ...this.getRequestData(),
                    segment_ids: this.fields.data.segment_ids ?? [],
                },
            })

            return `${detailsPreviewUrl}${encodeURIComponent(url)}`
        } catch (error) {
            return ""
        }
    }

    getListPreviewUrl = async () => {
        const listPreviewUrl = flutterBaseUrl + "notice-board-preview?base64="

        try {
            const url = await PreviewService.postV1PreviewMessageList({
                request: {
                    attachments: await this.getAttachments(),
                    ...this.getRequestData(),
                    segment_ids: [],
                },
            })

            return `${listPreviewUrl}${encodeURIComponent(url)}`
        } catch (error) {
            return ""
        }
    }

    @loads(() => NoticeBoardPostDetailStore.LoadingKeys.submit)
    async submit(mode: string) {
        const { data } = this.fields

        await this.fields.catchErrors(async () => {
            if (this.id == null || mode === "Copy") {
                this.validateFields()

                if (this.fields.hasErrors()) return
                const request: message_CreateMessageRequest = {
                    attachments: await this.getAttachments(),
                    segment_ids: data.segment_ids ?? [],
                    ...this.getRequestData(),
                }

                const response =
                    await MessageAdminService.noticeBoardCreateMessage({
                        request,
                    })

                if (response?.message_id != null) {
                    await MessageAdminService.noticeBoardPublishMessage({
                        messageId: response.message_id,
                        request: {
                            published_in: data.segment_ids,
                            dimension_ids: data.dimensionIds,
                        },
                    })

                    Channel.send({
                        name: "repository/updated",
                        payload: {
                            repository: "messages",
                            action: "create",
                            item: {
                                id: response.message_id ?? 0,
                                name: data.title,
                            },
                        },
                    })
                }
            } else {
                // TODO: remove validation check, when this is added in API
                // Update mode is missing required fields validation on API side. Only create mode has it.
                this.validateFields()
                if (this.fields.hasErrors()) return

                let updateRequest: message_UpdateMessage = {
                    attachments: await this.getAttachments(),
                    ...this.getRequestData(),
                }

                await MessageAdminService.noticeBoardUpdateMessage({
                    request: updateRequest,
                    messageId: this.id,
                })

                await MessageAdminService.noticeBoardPublishMessage({
                    messageId: this.id,
                    request: {
                        published_in: data.segment_ids,
                        dimension_ids: data.dimensionIds,
                    },
                })

                Channel.send({
                    name: "repository/updated",
                    payload: {
                        repository: "messages",
                        action: "update",
                        item: {
                            id: this.id,
                            name: data.title,
                        },
                    },
                })
            }
        })
    }

    private validateFields(): void {
        this.fields.validateRequiredFields([
            { field: "title" },
            { field: "admin_title" },
            { field: "accessGroupId" },
        ])
    }

    private getRequestData() {
        const { data } = this.fields

        return {
            access_group_id: this.fields.get("accessGroupId") ?? 0,
            admin_title: data.admin_title.trim(),
            pinned: data.pinned,
            publish_at: data.publish_at?.toISOString(),
            send_push: data.send_push,
            text: data.text,
            title: data.title.trim(),
            unpublish_at: data.unpublish_at?.toISOString(),
            include_in_chatbot: data.include_in_chatbot,
            ...(data.event_starts_at !== null && {
                event_starts_at: data.event_starts_at.toISOString(),
            }),
            ...(data.event_ends_at !== null && {
                event_ends_at: data.event_ends_at.toISOString(),
            }),
        }
    }

    private getAttachments = async () => {
        const { data } = this.fields

        return this.toMessageAttachments(
            await this.persistAttachments(
                data.documentAttachments,
                data.imageAttachments,
            ),
        )
    }

    private setId(id?: number) {
        this.id = id
    }

    private toMessageAttachments(
        files: IPersistedNoticeBoardPostFile[],
    ): message_CreateMessageAttachment[] {
        return files.map((file) => ({
            name: file.name,
            url: file.url,
            // The api docs are incorrect so we need to type assert this.
            attachment_type:
                file.type as message_CreateMessageAttachment["attachment_type"],
        }))
    }

    private toPersistedFiles(
        messageAttachments: message_MessageAttachment[],
        type: "image" | "doc",
    ) {
        return messageAttachments
            .filter((attachment) => attachment.attachment_type === type)
            .map((attachment) => this.toPesistedFile(attachment, type))
    }

    private toPesistedFile(
        messageAttachment: message_MessageAttachment,
        type: "image" | "doc",
    ): IPersistedFile {
        return {
            name: messageAttachment.name ?? "",
            url: messageAttachment.url ?? "",
            // The document type is called "doc" for this particular api. Rename
            // it to "document", to be consistent with other api:s.
            type: type === "doc" ? "document" : type,
        }
    }

    private async persistAttachments(
        documents: IFile[],
        images: IFile[],
    ): Promise<IPersistedNoticeBoardPostFile[]> {
        const persistedDocuments = await persistFiles(documents, "document")
        const persistedImages = await persistFiles(images, "image")
        return [...persistedDocuments, ...persistedImages].map((file) => ({
            ...file,
            // The document type is called "doc" for this particular api. Map
            // back type for documents from `document` to `doc`.
            type: file.type === "document" ? ("doc" as const) : file.type,
        }))
    }
}
